import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCorners, PointerSensor, TouchSensor, useSensor, useSensors, useDroppable, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, Search, Filter, X as XIcon, Trash2, Layers, FileText, FolderOpen, Users, AlertCircle, CheckCircle2, MoreVertical, Clock, Activity, RefreshCw, CheckCircle, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { Textarea } from '../components/ui/textarea';
import CountUp from 'react-countup';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { isUserEffectivelyOnTask } from '../utils/taskScope';
import { formatRichText } from '../utils/markdown';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
  { id: 'beklemede', label: 'Beklemede', color: 'bg-gray-100 border-gray-300', textColor: 'text-gray-700', icon: Clock, aliases: ['pending', 'beklemede'] },
  { id: 'işlemde', label: 'İşlemde', color: 'bg-gray-100 border-gray-300', textColor: 'text-blue-700', icon: Activity, aliases: ['in_progress', 'işlemde'] },
  { id: 'incelemede', label: 'İncelemede', color: 'bg-gray-100 border-gray-300', textColor: 'text-amber-500', icon: RefreshCw, aliases: ['in_review', 'incelemede', 'revizyon_gerekli'] },
  { id: 'tamamlandı', label: 'Tamamlandı', color: 'bg-gray-100 border-gray-300', textColor: 'text-green-700', icon: CheckCircle, aliases: ['completed', 'tamamlandı'] },
];

// Helper function to normalize status
const normalizeStatus = (status) => {
  const statusLower = (status || '').toLowerCase();
  for (const statusDef of STATUSES) {
    if (statusDef.aliases.some(alias => alias.toLowerCase() === statusLower)) {
      return statusDef.id;
    }
  }
  return 'beklemede'; // default
};

const PRIORITIES = [
  { value: 'low', label: 'Düşük', color: 'bg-green-100 text-green-700', hexColor: '#22c55e' }, // Yeşil
  { value: 'medium', label: 'Orta', color: 'bg-yellow-100 text-yellow-700', hexColor: '#eab308' }, // Sarı
  { value: 'high', label: 'Yüksek', color: 'bg-orange-100 text-orange-700', hexColor: '#f97316' }, // Turuncu
  { value: 'urgent', label: 'Acil', color: 'bg-red-200 text-red-900', hexColor: '#ef4444' }, // Kırmızı
];

// Draggable Task Card Component
const TaskCard = React.memo(function TaskCard({ task, canMove, canDelete, onClick, onDelete, onMoveTask, users, unreadCount, tags, user }) {
  const [showMobileMenu, setShowMobileMenu] = React.useState(false);
  const menuRef = React.useRef(null);
  
  // Elle taşımayı herkese aç - permission kontrolü handleDragEnd'de yapılacak
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ 
    id: task.id,
    disabled: !canMove
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const priorityInfo = PRIORITIES.find(p => p.value === task.priority);
  
  // Etiket bilgisini bul
  const taskTag = tags?.find(t => t.id === task.tagId);

  // Dışarı tıklama ile menüyü kapat
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
    };

    if (showMobileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showMobileMenu]);

  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onDelete) {
      onDelete(task);
    }
  };

  const handleMobileMenuToggle = (e) => {
    e.stopPropagation();
    setShowMobileMenu(!showMobileMenu);
  };

  const handleMobileMove = (e, newStatus) => {
    e.stopPropagation();
    setShowMobileMenu(false);
    if (onMoveTask) {
      onMoveTask(task.id, newStatus);
    }
  };

  // Debug: Revizyon durumunu konsola yazdır
  // originalStatus kullan çünkü task.status normalize edilmiş olabilir
  const isRevisionNeeded = task.originalStatus === 'revizyon_gerekli' || task.currentRevision;

  // Normalize status for display
  const normalizedStatus = normalizeStatus(task.status);
  const canMoveFromCurrentStatus = normalizedStatus === 'beklemede' || normalizedStatus === 'işlemde';

  // Mobilde drag-and-drop'u kapat - sadece desktop'ta aktif
  const [isMobile, setIsMobile] = React.useState(false);
  
  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...(isMobile ? {} : attributes)}
      {...(isMobile ? {} : listeners)}
      className={`bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow relative group ${
        canMove && !isMobile ? 'cursor-move' : 'cursor-pointer'
      }`}
      onClick={onClick}
    >
      {/* Revizyon Gerekli Badge'i - En üstte sağda */}
      {isRevisionNeeded && (
        <div className="absolute top-2 right-2 z-10">
          <div className="bg-orange-500 text-white text-xs font-bold px-2.5 py-1 rounded-md shadow-lg animate-pulse">
            REV
          </div>
        </div>
      )}
      
      {/* Okunmamış Bildirim Badge'i - Revizyon badge'i varsa alta kaydır */}
      {unreadCount > 0 && (
        <div className={`absolute ${isRevisionNeeded ? 'top-11' : 'top-2'} right-2 z-10`}>
          <div className="relative animate-pulse">
            <div className="w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xs font-bold">{unreadCount}</span>
            </div>
            <div className="absolute inset-0 w-8 h-8 bg-red-400 rounded-full animate-ping opacity-75"></div>
          </div>
        </div>
      )}
      
      {/* Menü Butonu - BEKLEMEDE herkes, diğer kolonlarda sadece Admin ve PM */}
      {canMove && (
        <div ref={menuRef} className="absolute top-2 left-2 z-20">
          <button
            onClick={(e) => {
              // BEKLEMEDE ise herkes taşıyabilir, diğer kolonlarda sadece admin ve proje_yoneticisi
              const canUserMove = normalizedStatus === 'beklemede' || user?.role === 'admin' || user?.role === 'proje_yoneticisi';
              if (canMoveFromCurrentStatus && canUserMove) {
                handleMobileMenuToggle(e);
              }
            }}
            className={`p-1 ${
              canMoveFromCurrentStatus && (normalizedStatus === 'beklemede' || user?.role === 'admin' || user?.role === 'proje_yoneticisi')
                ? 'text-gray-600 hover:text-gray-900 cursor-pointer' 
                : 'text-gray-300 cursor-not-allowed opacity-50'
            }`}
            title={
              !canMoveFromCurrentStatus
                ? 'Bu kolondan taşıma yapılamaz'
                : normalizedStatus !== 'beklemede' && user?.role !== 'admin' && user?.role !== 'proje_yoneticisi'
                  ? 'Yalnızca Admin ve Proje Yöneticisi taşıyabilir'
                  : 'Görev Taşı'
            }
            disabled={!canMoveFromCurrentStatus || (normalizedStatus !== 'beklemede' && user?.role !== 'admin' && user?.role !== 'proje_yoneticisi')}
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          
          {/* Dropdown Menu - BEKLEMEDE herkes, diğer kolonlarda sadece Admin ve PM */}
          {showMobileMenu && canMoveFromCurrentStatus && (normalizedStatus === 'beklemede' || user?.role === 'admin' || user?.role === 'proje_yoneticisi') && (
            <div className="absolute top-10 left-0 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[180px] z-30">
              <div className="py-1">
                <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b">
                  Görev Taşı
                </div>
                {normalizedStatus !== 'beklemede' && (
                  <button
                    onClick={(e) => handleMobileMove(e, 'beklemede')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                    Beklemede
                  </button>
                )}
                {normalizedStatus !== 'işlemde' && (
                  <button
                    onClick={(e) => handleMobileMove(e, 'işlemde')}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                  >
                    <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                    İşlemde
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {task.projectName && (
        <div className="text-xs text-gray-700 font-medium mb-1 pl-6 flex items-center gap-1">
          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
          {task.projectName}
        </div>
      )}
      <div className="mb-2">
        <div className="inline-flex items-start gap-2 max-w-full">
          <h4 className="font-semibold text-base text-gray-900 line-clamp-2 inline" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>{task.title}</h4>
          {canDelete && (
            <button
              onClick={handleDelete}
              className="opacity-0 group-hover:opacity-100 transition-colors text-gray-400 hover:text-red-600 flex-shrink-0"
              title="Görevi Sil"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
      {task.description && (
        <div
          className="text-sm text-gray-600 mb-3 line-clamp-3 break-words"
          dangerouslySetInnerHTML={formatRichText(task.description)}
        />
      )}
      
      {/* Miktar bilgisi gösterimi kaldırıldı - Görev detayda görülebilir */}
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {/* Öncelik Badge - Etiket ile aynı stil */}
          <div 
            className="inline-flex items-center px-1.5 h-6 rounded-md text-[10px] font-medium border-[1.5px]"
            style={{ 
              backgroundColor: `${priorityInfo?.hexColor}20`, 
              borderColor: priorityInfo?.hexColor,
              color: priorityInfo?.hexColor 
            }}
          >
            {priorityInfo?.label}
          </div>
          {/* Görüntülenme Sayısı Badge - viewCount > 0 ise göster */}
          {task.viewCount > 0 && (
            <div
              data-testid={`task-card-view-count-${task.id}`}
              className="inline-flex items-center px-1.5 h-6 rounded-md text-[10px] font-medium border border-gray-300 bg-gray-50 text-gray-700"
              title={`Görüntülenme: ${task.viewCount}`}
            >
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {task.viewCount}
            </div>
          )}
          {/* Kategori Badge - Kaldırıldı (workflow görevlerinde yanlış tag gösteriyordu) */}
        </div>
        {/* Assignment History - Kullanıcı Profil Resimleri */}
        {task.assignmentHistory && task.assignmentHistory.length > 0 && (
          <div className="flex items-center gap-1.5">
            {/* 🕒 Bu adımda kaç gündür bekliyor badge'i (avatar'ın solunda, görüntülenme badge'i ile aynı boyut) */}
            {task.currentStepEnteredAt && (task.currentStepDays !== null && task.currentStepDays !== undefined) && (() => {
              const days = task.currentStepDays;
              const label = days <= 0 ? 'Bugün' : `${days}g`;
              let cls = 'border-gray-300 bg-gray-50 text-gray-700';
              if (days >= 7) cls = 'border-red-300 bg-red-50 text-red-700';
              else if (days >= 3) cls = 'border-amber-300 bg-amber-50 text-amber-700';
              const enteredDate = task.currentStepEnteredLabel || '';
              return (
                <span
                  data-testid="step-waiting-days-badge"
                  className={`inline-flex items-center px-1.5 h-6 rounded-md text-[10px] font-medium border ${cls}`}
                  title={`Bu adımda ${days <= 0 ? '1 günden az' : days + ' gün'} bekliyor • Giriş: ${enteredDate}`}
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {label}
                </span>
              );
            })()}
            <div className="flex -space-x-2">
            {(() => {
              // ────────────────────────────────────────────────────────────
              // GÖRÜNÜM KURALI v3 (kullanıcı isteği):
              //   • Sadece TEK avatar göster = ŞU ANDAKİ aktif sorumlu.
              //   • 1. ÖNCELİK: task.assignedTo (backend single source of truth)
              //   • 2. YEDEK : assignmentHistory'de completedAt=null olan ilk kayıt
              //   • Hiçbiri yoksa avatar gösterilmiyor.
              // NOT: 1. öncelik task.assignedTo çünkü assignmentHistory'de
              //      bazen 2 NULL atama birden olabiliyor (veri tutarsızlığı).
              //      task.assignedTo backend tarafından her zaman doğru tutulur.
              // ────────────────────────────────────────────────────────────
              // BACKUP_IMPL (v0 — orijinal: en yeni 4 unique kullanıcı):
              //   const uniqueUsers = [];
              //   const seenIds = new Set();
              //   [...task.assignmentHistory].reverse().forEach(a => {
              //     if (a.userId && !seenIds.has(a.userId)) {
              //       seenIds.add(a.userId); uniqueUsers.push(a);
              //     }
              //   });
              //   const displayUsers = uniqueUsers.slice(0, 4);
              // ────────────────────────────────────────────────────────────
              // BACKUP_IMPL (v1 — aktif en solda, geri kalan kronolojik ters):
              //   const activeAssignment = (task.assignmentHistory || [])
              //     .find(a => a && !a.completedAt) || null;
              //   const completedReversed = [...(task.assignmentHistory || [])]
              //     .filter(a => a && a.completedAt).reverse();
              //   const ordered = []; const seen = new Set();
              //   const pushUnique = a => { if (a?.userId && !seen.has(a.userId))
              //     { seen.add(a.userId); ordered.push(a); } };
              //   pushUnique(activeAssignment); completedReversed.forEach(pushUnique);
              //   const displayUsers = ordered.slice(0, 4);

              const history = task.assignmentHistory || [];
              // 1. ÖNCELİK: task.assignedTo
              let activeUserId = task.assignedTo || null;
              let activeAssignment = activeUserId
                ? history.find((a) => a && a.userId === activeUserId && !a.completedAt)
                  || history.find((a) => a && a.userId === activeUserId)
                  || null
                : null;
              // 2. YEDEK: assignmentHistory'de ilk NULL completedAt
              if (!activeUserId) {
                const firstActive = history.find((a) => a && !a.completedAt);
                if (firstActive) {
                  activeAssignment = firstActive;
                  activeUserId = firstActive.userId;
                }
              }

              if (!activeUserId) return null;

              const displayUsers = [
                activeAssignment || { userId: activeUserId, userName: null }
              ];
              const remainingCount = 0;
              
              return (
                <>
                  {displayUsers.map((assignment, idx) => {
                    const user = users.find(u => u.id === assignment.userId);
                    const userName = user?.name || assignment.userName || 'Bilinmiyor';
                    const userPicture = user?.picture;
                    
                    // Kullanıcı ID'sine göre sabit renk ata
                    const avatarColors = [
                      'from-violet-400/90 to-purple-500/90',
                      'from-rose-400/90 to-pink-500/90',
                      'from-emerald-400/90 to-teal-500/90',
                      'from-blue-400/90 to-indigo-500/90',
                      'from-amber-400/90 to-orange-500/90',
                      'from-cyan-400/90 to-sky-500/90',
                      'from-fuchsia-400/90 to-pink-500/90',
                      'from-lime-400/90 to-green-500/90',
                    ];
                    const colorIndex = (assignment.userId || '').charCodeAt(0) % avatarColors.length;
                    const gradient = avatarColors[colorIndex];
                    
                    return (
                      <div 
                        key={`${assignment.userId}-${idx}`}
                        className={`w-8 h-8 rounded-md shadow-[0_3px_8px_-1px_rgba(0,0,0,0.18),0_1px_2px_-1px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_12px_-1px_rgba(0,0,0,0.25)] transition-shadow ${userPicture ? 'bg-transparent' : `bg-gradient-to-br ${gradient}`} flex items-center justify-center text-sm font-bold text-white overflow-hidden`}
                        style={{ marginLeft: idx > 0 ? '-6px' : '0', zIndex: displayUsers.length - idx }}
                        title={userName}
                      >
                        {userPicture ? (
                          <img 
                            src={userPicture.startsWith('http') ? userPicture : `${window.location.origin}${userPicture}`}
                            alt={userName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="drop-shadow-sm">{userName.charAt(0).toUpperCase()}</span>
                        )}
                      </div>
                    );
                  })}
                  {remainingCount > 0 && (
                    <div 
                      className="w-6 h-6 rounded-md border-[1.5px] border-white shadow-sm bg-gradient-to-br from-gray-400 to-gray-500 flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ marginLeft: '-6px', zIndex: 0 }}
                      title={`${remainingCount} diğer kullanıcı`}
                    >
                      +{remainingCount}
                    </div>
                  )}
                </>
              );
            })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

// Kanban Column Component
function KanbanColumn({ status, tasks, canMove, canDeleteTask, onDeleteTask, onMoveTask, users, unreadCounts, tags, onTaskClick, user }) {
  const statusInfo = STATUSES.find(s => s.id === status);
  const { setNodeRef } = useDroppable({
    id: status,
  });
  
  return (
    <div 
      ref={setNodeRef}
      id={status}
      data-column-id={status}
      className={`flex-1 min-w-[280px] rounded-xl ${statusInfo.color} border-2 p-4 transition-all duration-300`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {statusInfo.icon && (
            <div className={`w-10 h-10 ${statusInfo.color.split(' ')[0]} rounded-lg flex items-center justify-center`}>
              <statusInfo.icon className={`w-5 h-5 ${statusInfo.textColor}`} />
            </div>
          )}
          <h3 className={`font-semibold ${statusInfo.textColor} text-lg`}>
            {statusInfo.label}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {status === 'işlemde' && (() => {
            const revCount = tasks.filter(t => t.status === 'revizyon_gerekli' || t.currentRevision).length;
            if (!revCount) return null;
            return (
              <span
                className="bg-orange-500 text-white text-xs font-bold px-2 py-0.5 rounded-md shadow-sm animate-pulse"
                title={`${revCount} revizyon görevi`}
                data-testid="revision-count-badge"
              >
                {revCount} REV
              </span>
            );
          })()}
          <span className={`${statusInfo.textColor} text-2xl font-bold`}>
            <CountUp end={tasks.length} duration={1.5} />
          </span>
        </div>
      </div>
      
      <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 min-h-[200px] max-h-[1400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {[...tasks].sort((a, b) => {
            // 1. Önce revizyon gerekli olanları en üste çıkar
            const aHasRevision = a.status === 'revizyon_gerekli' || a.currentRevision;
            const bHasRevision = b.status === 'revizyon_gerekli' || b.currentRevision;
            if (aHasRevision && !bHasRevision) return -1;
            if (!aHasRevision && bHasRevision) return 1;
            
            // 2. Öncelik sırasına göre sırala (urgent > high > medium > low)
            const priorityOrder = { 'urgent': 4, 'high': 3, 'medium': 2, 'low': 1 };
            const aPriority = priorityOrder[a.priority] || 0;
            const bPriority = priorityOrder[b.priority] || 0;
            if (aPriority !== bPriority) return bPriority - aPriority;
            
            // 3. Güncelleme tarihine göre sırala (en yeni üstte)
            return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
          }).map(task => (
            <TaskCard
              key={task.id}
              task={task}
              canMove={canMove}
              canDelete={canDeleteTask(task)}
              onDelete={onDeleteTask}
              onMoveTask={onMoveTask}
              onClick={() => onTaskClick(task.id)}
              users={users}
              unreadCount={unreadCounts[task.id] || 0}
              tags={tags}
              user={user}
            />
          ))}
          {tasks.length === 0 && (
            <div className="text-center text-gray-400 text-sm py-8">
              Görev yok
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

export default function TaskBoard() {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [tags, setTags] = useState([]); // Etiketler
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({}); // {taskId: count}

  // Admin can use backend `view_as` to see another user's full RBAC scope
  const isAdmin = !!(
    user?.permissions?.includes('can_manage_users') &&
    user?.permissions?.includes('can_view_all_tasks')
  );
  
  // Filters — Dashboard'dan gelen URL parametrelerini ilk render'da senkron oku ki
  // loadData'nın ilk fetch'i doğru view_as ile yapılabilsin (race fix).
  const initialUrlParams = (() => {
    try { return new URLSearchParams(window.location.search); }
    catch { return new URLSearchParams(''); }
  })();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState(() => initialUrlParams.get('project') || 'all');
  const [filterPriority, setFilterPriority] = useState(() => initialUrlParams.get('priority') || 'all');
  const [filterAssignee, setFilterAssignee] = useState(() => initialUrlParams.get('assignee') || 'all');
  const [activeTasksOnly, setActiveTasksOnly] = useState(() => initialUrlParams.get('myActive') === '1');
  const [filterTag, setFilterTag] = useState(() => initialUrlParams.get('tag') || 'all'); // Etiket filtresi
  const [filterPeriod, setFilterPeriod] = useState(() => initialUrlParams.get('period') || 'all');
  const [customStartDate, setCustomStartDate] = useState(() => {
    const v = initialUrlParams.get('start_date');
    return v ? new Date(v) : null;
  });
  const [customEndDate, setCustomEndDate] = useState(() => {
    const v = initialUrlParams.get('end_date');
    return v ? new Date(v) : null;
  });
  const [showFilters, setShowFilters] = useState(false);
  const [commentSearch, setCommentSearch] = useState('');
  const [commentTaskIds, setCommentTaskIds] = useState(null);
  
  // New task dialog
  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const [newTaskData, setNewTaskData] = useState({
    title: '',
    description: '',
    projectId: '',
    workflowId: 'none',
    priority: 'medium',
    assignedTo: 'none',
    taskType: 'Üretim', // Manuel atamada görev tipi
    manualStepIndex: null, // Workflow'da hangi adıma manuel atama yapılacak
    quantityType: 'adet', // Miktar tipi: adet veya süre
    quantityValue: '1' // Miktar değeri
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Reset form to initial state
  const resetTaskForm = () => {
    setNewTaskData({
      title: '',
      description: '',
      projectId: '',
      workflowId: 'none',
      priority: 'medium',
      assignedTo: 'none',
      taskType: 'Üretim',
      manualStepIndex: null,
      quantityType: 'adet',
      quantityValue: '1'
    });
    setFormErrors({});
    setTouched({});
  };
  
  // Delete task dialog
  const [showDeleteTaskDialog, setShowDeleteTaskDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Active drag state
  const [activeId, setActiveId] = useState(null);
  
  // Drawer state for task detail (desktop only)
  const { selectedTaskId, isOpen: showTaskDrawer, drawerRef, openDrawer, closeDrawer: handleCloseTaskDrawer } = useTaskDrawer();

  // Drag & Drop sensors - Hem mouse hem touch desteği
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250, // 250ms basılı tutma
        tolerance: 5, // 5px hareket toleransı
      },
    })
  );

  // Dashboard'dan gelen hash kontrolü için - VERİ YÜKLENDİKTEN SONRA
  // Push notification'dan gelen URL parameter kontrolü + Dashboard filtre parametreleri
  const pendingScrollStatusRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get('openTask');
    
    if (taskIdParam) {
      console.log('📬 [Notification] Opening task:', taskIdParam);
      // Drawer'da farklı task açıksa önce kapat ki TaskDetail içeriği yeniden yüklensin
      if (showTaskDrawer && selectedTaskId && selectedTaskId !== taskIdParam) {
        handleCloseTaskDrawer();
        setTimeout(() => openDrawer(taskIdParam), 320);
      } else {
        openDrawer(taskIdParam);
      }
      // openTask paramını URL'den sil, ancak email'den gelen highlight/id/step
      // paramlarını KORU — TaskDetail bunları okuyup ilgili bölüme scroll eder.
      const preserved = new URLSearchParams();
      ['highlight', 'id', 'step'].forEach((k) => {
        const v = params.get(k);
        if (v) preserved.set(k, v);
      });
      const qs = preserved.toString();
      navigate(`/tasks${qs ? '?' + qs : ''}`, { replace: true });
      return;
    }
    
    // Dashboard'dan gelen filtre parametreleri — state lazy-init zaten okudu;
    // burada sadece "showFilters" gösterimi ve URL temizliği yapıyoruz.
    let hasFilters = false;
    const statusParam = params.get('status');

    if (params.get('project')) hasFilters = true;
    if (params.get('priority')) hasFilters = true;
    if (params.get('assignee')) hasFilters = true;
    if (params.get('tag')) hasFilters = true;
    if (params.get('period')) hasFilters = true;
    
    // Status parametresi gelirse — scroll'u loading bittikten sonra yap
    if (statusParam) {
      hasFilters = true;
      pendingScrollStatusRef.current = statusParam;
    }
    
    if (hasFilters) {
      navigate('/tasks', { replace: true });
    }
  }, [location.search]);

  // Loading bittiğinde Dashboard'dan gelen status'a scroll et
  useEffect(() => {
    if (loading) return;
    const target = pendingScrollStatusRef.current;
    if (!target) return;
    // DOM render edildikten sonra
    const timer = setTimeout(() => {
      const col = document.querySelector(`[data-column-id="${target}"]`);
      if (col) {
        // Manuel scroll: kolon başlığı görünecek şekilde 120px boşluk bırak
        const rect = col.getBoundingClientRect();
        const targetY = window.scrollY + rect.top - 120;
        window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
        col.classList.add('ring-4', 'ring-cyan-400', 'ring-offset-2');
        setTimeout(() => col.classList.remove('ring-4', 'ring-cyan-400', 'ring-offset-2'), 1800);
      }
      pendingScrollStatusRef.current = null;
    }, 200);
    return () => clearTimeout(timer);
  }, [loading]);

  // Service Worker'dan gelen mesajları dinle
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE') {
        if (event.data.taskId) {
          console.log('📬 [Push] Opening task drawer:', event.data.taskId);
          openDrawer(event.data.taskId);
        }
      }
    };
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
      
      return () => {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      };
    }
  }, []);

  useEffect(() => {
    // Sadece loading false olduğunda (veriler yüklendikten sonra) çalış
    if (!loading) {
      const hash = decodeURIComponent(window.location.hash.replace('#', ''));
      if (hash) {
        // Mobil cihazlar için daha uzun bekleme süresi
        const scrollDelay = window.innerWidth < 768 ? 1000 : 500;
        
        setTimeout(() => {
          const statusElement = document.getElementById(hash);
          
          if (statusElement) {
            // Mobil'de daha yumuşak scroll için 'start' kullan
            const scrollBlock = window.innerWidth < 768 ? 'start' : 'center';
            statusElement.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
            
            // Kısa bir highlight efekti
            statusElement.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2');
            setTimeout(() => {
              statusElement.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2');
            }, 2000);
          } else {
            console.warn('⚠️ Hash element bulunamadı:', hash);
          }
        }, scrollDelay);
      }
    }
  }, [loading]); // loading state'i değiştiğinde tetiklenir

  useEffect(() => {
    const loadData = async () => {
      try {
        // ⚡ PERFORMANCE OPTIMIZATION: Tüm kritik API çağrılarını paralel yap
        // Tasks normalize işlemi projects/users'a bağımlı ama fetch paralel olabilir.
        // İlk fetch'te admin için view_as parametresi (URL'den gelen filterAssignee).
        const initialViewAs = (isAdmin && filterAssignee !== 'all') ? filterAssignee : null;
        const [projectsData, usersData, , , tasksRaw] = await Promise.all([
          fetchProjects(),
          fetchUsers(),
          fetchWorkflows(),
          fetchTags(),
          fetchTasksRaw(initialViewAs)
        ]);
        // Paralel çağrılar tamamlanınca tasks'ı normalize et (projects/users data'sıyla)
        normalizeAndSetTasks(tasksRaw, projectsData, usersData);

        // Unread counts non-blocking: arka planda yükle, sayfa açılışını bloklama
        fetchUnreadCounts();
      } catch (error) {
        console.error('❌ Veri yükleme hatası:', error);
        toast.error('Veriler yüklenirken bir hata oluştu');
      }
    };
    loadData();
    
    // Her 30 saniyede bir okunmamış bildirim sayılarını güncelle
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  // Admin "view as user": backend'den o kullanıcının full RBAC scope'unu çek.
  // filterAssignee her değiştiğinde refetch yapar (admin için).
  // Ref ilk render'da mevcut filterAssignee değeriyle başlatılır (URL'den
  // gelmiş olabilir) → loadData ile çift fetch yapılmaz.
  const prevFilterAssigneeRef = useRef(filterAssignee);
  useEffect(() => {
    if (!isAdmin) return;
    // İlk render — loadData zaten ilk fetch'i yapacak; aynı 'all' değeri için tekrar etme
    if (prevFilterAssigneeRef.current === filterAssignee) return;
    prevFilterAssigneeRef.current = filterAssignee;
    const targetId = filterAssignee !== 'all' ? filterAssignee : null;
    fetchTasks(projects, users, targetId);
  }, [filterAssignee, isAdmin]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      console.log('📁 Projects fetched:', response.data.length, 'projects');
      console.log('📁 Project IDs:', response.data.map(p => ({ id: p.id, name: p.name })));
      // ✅ Alfabetik sıralama
      const sortedProjects = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setProjects(sortedProjects);
      return sortedProjects; // Return data for immediate use
    } catch (error) {
      console.error('Projects fetch error:', error);
      return [];
    }
  };

  // ⚡ Pure raw fetch (paralel kullanım için, projects/users beklemez).
  const fetchTasksRaw = async (viewAsUserId = null) => {
    try {
      setLoading(true);
      const url = viewAsUserId
        ? `${API}/tasks?view_as=${encodeURIComponent(viewAsUserId)}`
        : `${API}/tasks`;
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Tasks fetch error:', error);
      toast.error('Görevler yüklenemedi');
      setLoading(false);
      return [];
    }
  };

  // ⚡ Normalize ve state set — projects/users hazır olduktan sonra çağrılır.
  const normalizeAndSetTasks = (rawTasks, projectsData = projects, usersData = users) => {
    const normalizedTasks = (rawTasks || []).map(task => {
      const project = projectsData.find(p => p.id === task.projectId);
      const assignedUser = usersData.find(u => u.id === task.assignedTo);
      const assignedToName = assignedUser?.name || null;
      const populatedHistory = (task.assignmentHistory || []).map(historyEntry => {
        const u = usersData.find(uu => uu.id === historyEntry.userId);
        return {
          ...historyEntry,
          userName: u?.name || 'Bilinmeyen Kullanıcı'
        };
      });
      return {
        ...task,
        originalStatus: task.status,
        status: normalizeStatus(task.status),
        projectName: project?.name || '',
        assignedToName,
        assignmentHistory: populatedHistory
      };
    });
    setTasks(normalizedTasks);
    setLoading(false);
  };

  const fetchTasks = async (projectsData = projects, usersData = users, viewAsUserId = null) => {
    const raw = await fetchTasksRaw(viewAsUserId);
    normalizeAndSetTasks(raw, projectsData, usersData);
  };

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data);
      return response.data;
    } catch (error) {
      console.error('Workflows fetch failed');
      return [];
    }
  };

  // Yorum arama - debounce
  const commentSearchTimer = useRef(null);
  const handleCommentSearch = useCallback((value) => {
    setCommentSearch(value);
    if (commentSearchTimer.current) clearTimeout(commentSearchTimer.current);
    if (!value || value.trim().length < 2) {
      setCommentTaskIds(null);
      return;
    }
    commentSearchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/comments/search`, { params: { q: value.trim() } });
        setCommentTaskIds(res.data.taskIds);
      } catch { setCommentTaskIds(null); }
    }, 400);
  }, []);


  const fetchUsers = async () => {
    try {
      // ✅ Görev kartlarındaki avatar/isim çözümü için TÜM aktif kullanıcı
      // listesi gerekli. Filtre seçenekleri için ayrıca availableUsers kullanılır.
      const response = await axios.get(`${API}/users`);
      const sortedUsers = (response.data || []).sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setUsers(sortedUsers);
      // Filtre listesi: rol bazlı kısıtlanmış kullanıcılar
      try {
        const fr = await axios.get(`${API}/reports/available-users`);
        const sortedFilter = (fr.data || []).sort((a, b) =>
          a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
        );
        setAvailableUsers(sortedFilter);
      } catch (e) {
        // 403 vs. — fallback: filtreye sadece kendini koy
        setAvailableUsers(user ? [user] : []);
      }
      return sortedUsers;
    } catch (error) {
      console.error('Users fetch failed');
      return [];
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/tags`);
      // ✅ Alfabetik sıralama
      const sortedTags = response.data.sort((a, b) => 
        a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })
      );
      setTags(sortedTags);
      return sortedTags;
    } catch (error) {
      console.error('Tags fetch failed');
      return [];
    }
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCounts(response.data);
    } catch (error) {
      console.error('Unread counts fetch failed');
    }
  };


  // Helper functions - must be defined before useMemo
  const canDeleteTask = (task) => {
    if (!user || !task) return false;
    // Check if user has can_delete_tasks permission
    return user.permissions?.includes('can_delete_tasks');
  };

  const handleDeleteTaskClick = (task) => {
    setTaskToDelete(task);
    setShowDeleteTaskDialog(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete || !canDeleteTask(taskToDelete)) {
      toast.error('Bu görevi silme yetkiniz yok');
      return;
    }

    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${taskToDelete.id}`);
      toast.success('Görev silindi');
      setShowDeleteTaskDialog(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      console.error('Delete task error:', error);
      toast.error(error.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!newTaskData.title.trim()) {
      errors.title = 'Görev başlığı zorunludur';
    }
    
    if (!newTaskData.projectId) {
      errors.projectId = 'Proje seçimi zorunludur';
    }
    
    const hasWorkflow = newTaskData.workflowId && newTaskData.workflowId !== 'none';
    const hasAssignee = newTaskData.assignedTo && newTaskData.assignedTo !== 'none';
    
    if (!hasWorkflow && !hasAssignee) {
      errors.workflowOrAssignee = 'İş akışı veya atanan kişiden en az biri zorunludur';
    }
    
    // Direkt atamada (workflow yok) kategori zorunlu
    if (!hasWorkflow && hasAssignee && !newTaskData.tagId) {
      errors.tagId = 'Direkt atamada kategori seçimi zorunludur';
    }
    
    return errors;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    setTouched({
      title: true,
      projectId: true,
      workflowOrAssignee: true,
      tagId: true
    });
    
    // Validate
    const errors = validateForm();
    setFormErrors(errors);
    
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    try {
      // Prepare task data - remove empty/none values
      const taskData = {
        title: newTaskData.title,
        description: newTaskData.description,
        projectId: newTaskData.projectId,
        priority: newTaskData.priority
      };
      
      // Only add workflowId if it's not 'none' or empty
      if (newTaskData.workflowId && newTaskData.workflowId !== 'none') {
        taskData.workflowId = newTaskData.workflowId;
      }
      
      // Only add assignedTo if it's not 'none' or empty
      if (newTaskData.assignedTo && newTaskData.assignedTo !== 'none') {
        taskData.assignedTo = newTaskData.assignedTo;
        
        // Eğer workflow varsa ve manuel adım seçilmişse
        if (newTaskData.workflowId && newTaskData.workflowId !== 'none' && newTaskData.manualStepIndex !== null) {
          taskData.manualStepIndex = newTaskData.manualStepIndex;
          taskData.taskType = newTaskData.taskType; // Seçilen adımın tipi
        } else if (!newTaskData.workflowId || newTaskData.workflowId === 'none') {
          // Workflow yoksa taskType, quantityType, quantityValue ve tagId gönder
          taskData.taskType = newTaskData.taskType;
          taskData.quantityType = newTaskData.quantityType;
          taskData.quantityValue = newTaskData.quantityValue;
          taskData.tagId = newTaskData.tagId; // Direkt atamada seçilen kategori
        }
      }
      
      await axios.post(`${API}/tasks`, taskData);
      toast.success('Görev oluşturuldu');
      setShowNewTaskDialog(false);
      resetTaskForm();
      fetchTasks();
    } catch (error) {
      console.error('Task creation error:', error.response?.data);
      
      // Handle error message properly
      let errorMessage = 'Görev oluşturulamadı';
      if (error.response?.data) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation error format
          errorMessage = error.response.data.detail.map(err => err.msg).join(', ');
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    console.log('Drag ended:', { activeId: active.id, overId: over?.id });
    
    setActiveId(null); // Clear active drag
    
    if (!over) {
      console.log('No drop target');
      return;
    }

    const task = tasks.find(t => t.id === active.id);
    if (!task) {
      console.log('Task not found:', active.id);
      return;
    }
    
    console.log('Dragged task:', { id: task.id, title: task.title, currentStatus: task.status });
    
    // Sadece "Beklemede" ve "İşlemde" kolonlarından sürüklenebilir
    // Status değerlerini normalize et
    const normalizedStatus = normalizeStatus(task.status);
    if (normalizedStatus !== 'beklemede' && normalizedStatus !== 'işlemde') {
      console.log('Drag not allowed from status:', task.status, 'normalized:', normalizedStatus);
      // Kısıtlı kolondan sürüklendiğinde görev mevcut kutusuna geri döner (otomatik)
      return;
    }
    
    // Determine new status: over.id can be either a status or a task id
    let newStatus = over.id;
    
    // If over.id is a task id, find that task's status
    const overTask = tasks.find(t => t.id === over.id);
    if (overTask) {
      console.log('Dropped on task:', overTask.title, 'status:', overTask.status);
      newStatus = overTask.status;
    } else {
      console.log('Dropped on column:', over.id);
    }
    
    console.log('New status determined:', newStatus);
    
    // Hedef status kontrolü: Sadece "beklemede" ve "işlemde" kolonlarına bırakılabilir
    const normalizedNewStatus = normalizeStatus(newStatus);
    if (normalizedNewStatus !== 'beklemede' && normalizedNewStatus !== 'işlemde') {
      console.log('Drop not allowed to status:', newStatus, 'normalized:', normalizedNewStatus);
      // Kısıtlı kolona bırakıldığında görev mevcut kutusuna geri döner (otomatik)
      return;
    }
    
    // If status hasn't changed, do nothing
    if (task.status === newStatus) {
      console.log('Status unchanged, no action needed');
      return;
    }

    // Check permission - Role-based control
    // BEKLEMEDE kolonundan herkes taşıyabilir, diğer kolonlardan sadece Admin ve PM
    let canMove = false;
    if (normalizedStatus === 'beklemede') {
      // BEKLEMEDE kolonundan herkes taşıyabilir
      canMove = true;
    } else {
      // Diğer kolonlardan sadece Admin ve Proje Yöneticisi taşıyabilir
      canMove = user?.role === 'admin' || user?.role === 'proje_yoneticisi';
    }

    console.log('Permission check:', { 
      canMove, 
      userRole: user.role, 
      taskStatus: normalizedStatus, 
      isFromBeklemede: normalizedStatus === 'beklemede' 
    });

    if (!canMove) {
      toast.error(normalizedStatus === 'beklemede' 
        ? 'Bu görevi taşıma yetkiniz yok' 
        : 'Yalnızca Admin ve Proje Yöneticisi bu görevleri taşıyabilir');
      return;
    }

    console.log('Moving task:', { taskId: active.id, from: task.status, to: newStatus });

    // Optimistic update
    setTasks(prev => prev.map(t => 
      t.id === active.id ? { ...t, status: newStatus } : t
    ));

    try {
      await axios.patch(`${API}/tasks/${active.id}`, { status: newStatus });
      toast.success('Görev durumu güncellendi');
      console.log('Task updated successfully');
    } catch (error) {
      console.error('Task update error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
      // Revert on error
      fetchTasks();
    }
  };

  // Görev tıklama handler'ı
  const handleTaskClick = (taskId) => {
    openDrawer(taskId);
  };

  // Manuel görev taşıma fonksiyonu (mobil için)
  const handleMoveTask = async (taskId, newStatus) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Yetki kontrolü
    const canMove = user.permissions?.includes('can_move_tasks') || (task.assignedTo === user.id);
    if (!canMove) {
      toast.error('Bu görevi taşıma yetkiniz yok');
      return;
    }

    // Aynı status'taysa işlem yapma
    if (task.status === newStatus) return;

    // Optimistic update
    setTasks(prev => prev.map(t => 
      t.id === taskId ? { ...t, status: newStatus } : t
    ));

    try {
      await axios.patch(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success('Görev taşındı');
    } catch (error) {
      console.error('Task move error:', error);
      toast.error('Görev taşınamadı');
      // Revert on error
      fetchTasks();
    }
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (commentTaskIds !== null && !commentTaskIds.includes(task.id)) return false;
      if (filterProject !== 'all' && task.projectId !== filterProject) return false;
      if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
      
      // Kullanıcı filtresi — backend RBAC scope ile birebir aynı kuralı uygular
      // (etkili atama: per-task override + runtime history + future template).
      // Admin için backend'den view_as ile çekildiğinden client-side filtre atlanır
      // (admin için backend zaten o kullanıcının full RBAC scope'unu döndürür).
      if (filterAssignee !== 'all' && !isAdmin) {
        if (!isUserEffectivelyOnTask(task, workflows, filterAssignee)) return false;
      }
      
      // Aktif Görevlerim filtresi - sadece şu anda assignedTo olan görevler
      // VE status aktif olmalı (tamamlandı/iptal hariç)
      if (activeTasksOnly) {
        if (task.assignedTo !== user?.id) return false;
        const activeStatuses = ['beklemede', 'işlemde', 'incelemede'];
        if (!activeStatuses.includes(task.status)) return false;
      }
      
      // Kategori filtresi - workflow adımlarındaki tagId'leri de kontrol et
      if (filterTag !== 'all') {
        const hasTaskTag = task.tagId === filterTag;
        const hasAssignmentTag = task.assignmentHistory && task.assignmentHistory.some(
          a => a.completedTagId === filterTag || a.tagId === filterTag
        );
        // Workflow'daki tüm adımların tagId'lerini kontrol et
        const taskWorkflow = task.workflowId && workflows.find(w => w.id === task.workflowId);
        const hasWorkflowTag = taskWorkflow && taskWorkflow.steps && taskWorkflow.steps.some(
          step => step.tagId === filterTag
        );
        if (!hasTaskTag && !hasAssignmentTag && !hasWorkflowTag) return false;
      }

      // Tarih filtresi (updatedAt ile son aktiviteye göre filtrele)
      if (filterPeriod !== 'all') {
        const dateStr = task.updatedAt || task.createdAt;
        if (!dateStr) return false;
        const taskDate = new Date(dateStr);
        const now = new Date();
        if (filterPeriod === 'this_month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          if (taskDate < monthStart) return false;
        } else if (filterPeriod === 'prev_month') {
          const monthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const monthEnd = new Date(now.getFullYear(), now.getMonth(), 1);
          if (taskDate < monthStart || taskDate >= monthEnd) return false;
        } else if (filterPeriod === 'this_year') {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          if (taskDate < yearStart) return false;
        } else if (filterPeriod === 'prev_year') {
          const yearStart = new Date(now.getFullYear() - 1, 0, 1);
          const yearEnd = new Date(now.getFullYear(), 0, 1);
          if (taskDate < yearStart || taskDate >= yearEnd) return false;
        } else if (filterPeriod === 'custom' && customStartDate && customEndDate) {
          const start = new Date(customStartDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(customEndDate);
          end.setHours(23, 59, 59, 999);
          if (taskDate < start || taskDate > end) return false;
        }
      }
      
      return true;
    });
  }, [tasks, searchQuery, filterProject, filterPriority, filterAssignee, filterTag, filterPeriod, customStartDate, customEndDate, commentTaskIds, activeTasksOnly, user?.id]);

  // Group tasks by status
  const tasksByStatus = useMemo(() => {
    const grouped = STATUSES.reduce((acc, status) => {
      const statusTasks = filteredTasks.filter(task => task.status === status.id);
      
      // "İşlemde" sütununda revizyon görevlerini en üste koy, sonra öncelik ve güncelleme tarihine göre
      if (status.id === 'işlemde') {
        statusTasks.sort((a, b) => {
          // currentRevision varsa revizyon görevi demektir
          const aIsRevision = a.currentRevision ? 1 : 0;
          const bIsRevision = b.currentRevision ? 1 : 0;
          
          // Revizyon görevleri önce gelsin
          if (bIsRevision !== aIsRevision) {
            return bIsRevision - aIsRevision;
          }
          
          // Aynı revizyon durumunda ise öncelik sırasına göre
          const priorityOrder = { 'urgent': 4, 'high': 3, 'medium': 2, 'low': 1 };
          const aPriority = priorityOrder[a.priority] || 0;
          const bPriority = priorityOrder[b.priority] || 0;
          if (bPriority !== aPriority) return bPriority - aPriority;
          
          // Güncelleme tarihine göre sırala (en yeni üstte)
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        });
      } else {
        // Diğer sütunlarda güncelleme tarihine göre sırala (en yeni üstte)
        statusTasks.sort((a, b) => {
          return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
        });
      }
      
      acc[status.id] = statusTasks;
      return acc;
    }, {});
    
    // Debug logging
    console.log('Tasks grouped:', {
      total: tasks.length,
      filtered: filteredTasks.length,
      byStatus: Object.entries(grouped).map(([status, tasks]) => ({ status, count: tasks.length }))
    });
    
    return grouped;
  }, [filteredTasks, tasks.length]);

  // Check if user can move tasks
  const canMoveAnyTask = user?.permissions?.includes('can_move_tasks');

  const clearFilters = () => {
    setSearchQuery('');
    setFilterProject('all');
    setFilterPriority('all');
    setFilterAssignee('all');
    setFilterTag('all');
    setCommentSearch('');
    setCommentTaskIds(null);
    setActiveTasksOnly(false);
  };

  const activeFilterCount = [
    filterProject !== 'all',
    filterPriority !== 'all',
    filterAssignee !== 'all',
    filterTag !== 'all', // Etiket filtresini say
    commentTaskIds !== null, // Yorum araması aktif
  ].filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 sm:mb-6 pt-4 pb-4 sm:pt-6 sm:pb-6 lg:pt-8 lg:pb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <Layers className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <span className="relative">
              Görevler
              {/* Dairesel badge - görev sayısı */}
              <span className="absolute -top-1 left-[100%] ml-1 min-w-[1.5rem] sm:min-w-[1.75rem] h-6 sm:h-7 px-2 bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold rounded-md flex items-center justify-center shadow-lg whitespace-nowrap">
                {filteredTasks.length.toLocaleString('tr-TR')}
              </span>
            </span>
          </h1>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Kanban Board ile görevlerinizi yönetin
            {filteredTasks.length > 0 && (
              <span className="text-blue-600 font-medium text-xs sm:text-sm"> • {filteredTasks.length.toLocaleString('tr-TR')} görev</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Aktif Görevlerim Butonu */}
          <Button
            data-testid="my-tasks-toggle"
            variant={activeTasksOnly ? "default" : "outline"}
            size="sm"
            onClick={() => {
              if (activeTasksOnly) {
                clearFilters();
              } else {
                setActiveTasksOnly(true);
              }
            }}
            className={`flex-1 sm:flex-none ${activeTasksOnly
              ? "bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white shadow-md shadow-blue-200"
              : "border-red-500 text-red-600 hover:bg-red-50 hover:text-red-700"
            }`}
          >
            <User className="w-4 h-4 mr-2" />
            {activeTasksOnly ? 'Tüm Görevler' : 'Aktif Görevlerim'}
          </Button>
          {/* Kategori Yönetimi Butonu - can_manage_tags yetkisi olanlar görebilir */}
          {user?.permissions?.includes('can_manage_tags') && (
            <Button 
              onClick={() => window.location.href = '/tags'} 
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-none border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7.5 14.25v2.25m3-4.5v4.5m3-6.75v6.75m3-9v9M6 20.25h12A2.25 2.25 0 0020.25 18V6A2.25 2.25 0 0018 3.75H6A2.25 2.25 0 003.75 6v12A2.25 2.25 0 006 20.25z" />
              </svg>
              Kategori Yönetimi
            </Button>
          )}
          {/* Yeni görev oluşturma yetkisi olan kullanıcılar görebilir */}
          {user?.permissions?.includes('can_create_tasks') && (
            <Button size="sm" onClick={() => {
              resetTaskForm();
              setShowNewTaskDialog(true);
            }} className="w-full sm:w-auto bg-gradient-to-r from-cyan-600 to-blue-600">
              <Plus className="w-4 h-4 mr-2" />
              Yeni Görev
            </Button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="glass rounded-xl p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Arama input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Görev ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Yorumlarda ara */}
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
            <Input
              data-testid="comment-search-input"
              value={commentSearch}
              onChange={(e) => handleCommentSearch(e.target.value)}
              placeholder="Yorumlarda ara..."
              className="pl-10 pr-8"
            />
            {commentSearch && (
              <button
                onClick={() => { setCommentSearch(''); setCommentTaskIds(null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            )}
            {/* yorum arama sonuç sayısı kaldırıldı */}
          </div>
          
          {/* Filtreler ve Yenile butonları - mobilde tam genişlik, desktop'ta otomatik */}
          <div className="flex gap-2 items-center w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="relative whitespace-nowrap flex-1 sm:flex-initial"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtreler
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-md w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </Button>
            
            <button
              onClick={() => {
                // Admin için server-side filtre olan filterAssignee (view_as) korunmalı
                const viewAsId = (isAdmin && filterAssignee !== 'all') ? filterAssignee : null;
                fetchTasks(projects, users, viewAsId);
              }}
              className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors flex-shrink-0"
              title="Verileri Yenile"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            {activeFilterCount > 0 && (
              <Button variant="ghost" onClick={clearFilters} className="whitespace-nowrap flex-shrink-0">
                <XIcon className="w-4 h-4 mr-2" />
                Temizle
              </Button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 pt-3 border-t">
            <DateRangeFilter
              period={filterPeriod}
              onPeriodChange={setFilterPeriod}
              startDate={customStartDate}
              endDate={customEndDate}
              onStartDateChange={setCustomStartDate}
              onEndDateChange={setCustomEndDate}
            />
            <div>
              <Label className="text-sm mb-2 block">Proje</Label>
              <Select value={filterProject} onValueChange={setFilterProject}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Projeler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Projeler</SelectItem>
                  {[...projects].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Öncelik</Label>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Öncelikler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Öncelikler</SelectItem>
                  {PRIORITIES.map(p => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Atanan Kişi</Label>
              <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Kullanıcılar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kullanıcılar</SelectItem>
                  {[...availableUsers].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm mb-2 block">Kategori</Label>
              <Select value={filterTag} onValueChange={setFilterTag}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Kategoriler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Kategoriler</SelectItem>
                  {tags.map(tag => (
                    <SelectItem key={tag.id} value={tag.id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: tag.color }}
                        />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-wrap gap-4 pb-4">
          {STATUSES.map(status => (
            <KanbanColumn
              key={status.id}
              status={status.id}
              tasks={tasksByStatus[status.id] || []}
              canMove={canMoveAnyTask}
              canDeleteTask={canDeleteTask}
              onDeleteTask={handleDeleteTaskClick}
              onMoveTask={handleMoveTask}
              users={users}
              unreadCounts={unreadCounts}
              tags={tags}
              onTaskClick={handleTaskClick}
              user={user}
            />
          ))}
        </div>
        
        <DragOverlay>
          {activeId ? (
            <div className="bg-white p-4 rounded-lg shadow-xl border-2 border-blue-500 opacity-90">
              {(() => {
                const task = tasks.find(t => t.id === activeId);
                if (!task) return null;
                const priorityInfo = PRIORITIES.find(p => p.value === task.priority);
                return (
                  <>
                    <h4 className="font-medium text-gray-900 mb-2" style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>{task.title}</h4>
                    {task.description && (
                      <div
                        className="text-sm text-gray-600 mb-3 line-clamp-3 break-words"
                        dangerouslySetInnerHTML={formatRichText(task.description)}
                      />
                    )}
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] px-1.5 h-6 inline-flex items-center rounded-md ${priorityInfo?.color}`}>
                        {priorityInfo?.label}
                      </span>
                      {task.assignedTo && (
                        <div className="w-6 h-6 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-md flex items-center justify-center text-xs text-white">
                          {task.assignedToName?.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* New Task Dialog */}
      <Dialog open={showNewTaskDialog} onOpenChange={(open) => {
        setShowNewTaskDialog(open);
        if (!open) {
          resetTaskForm();
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {/* Close Button */}
          <button
            onClick={() => setShowNewTaskDialog(false)}
            className="absolute right-3 top-3 z-50 p-1.5 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all"
          >
            <XIcon className="w-4 h-4 text-gray-600 hover:text-gray-900" />
          </button>
          
          {/* Modern Gradient Header */}
          <DialogHeader className="bg-gradient-to-r from-cyan-600 via-blue-600 to-cyan-700 -m-6 mb-0 px-6 py-5 rounded-t-lg">
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Plus className="w-6 h-6 text-white" />
              </div>
              Yeni Görev Oluştur
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateTask} className="space-y-6 py-6 px-1">
            {/* Başlık */}
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-5 rounded-xl border-2 border-cyan-200">
              <Label htmlFor="title" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-cyan-600" />
                Görev Başlığı <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                value={newTaskData.title}
                onChange={(e) => {
                  setNewTaskData({ ...newTaskData, title: e.target.value });
                  if (touched.title) {
                    setFormErrors({...formErrors, title: null});
                  }
                }}
                onBlur={() => setTouched({...touched, title: true})}
                placeholder="Örn: Logo tasarımı yap"
                className={`bg-white border-2 ${formErrors.title && touched.title ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-cyan-300 focus:border-cyan-500 focus:ring-cyan-500'}`}
              />
              {formErrors.title && touched.title && (
                <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{formErrors.title}</span>
                </div>
              )}
            </div>
            {/* Açıklama */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border-2 border-purple-200">
              <Label htmlFor="description" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <FileText className="w-5 h-5 text-purple-600" />
                Açıklama
              </Label>
              <Textarea
                id="description"
                value={newTaskData.description}
                onChange={(e) => setNewTaskData({ ...newTaskData, description: e.target.value })}
                placeholder={`Görev detaylarını açıklayın...\n\nFormatlama:\n**kalın**  *italik*\n## Başlık\n- Madde listesi`}
                className="bg-white border-2 border-purple-300 focus:border-purple-500 focus:ring-purple-500 resize-none"
                rows={6}
              />
              <div className="mt-2 text-xs text-purple-600 font-medium">
                **kalın**  *italik*  ## Başlık  - Madde listesi
              </div>
            </div>

            {/* 2x2 Grid: Proje/İş Akışı (üst satır), Öncelik/Atanan Kişi (alt satır) */}
            <div className="grid grid-cols-2 gap-4">
              {/* Sol Sütun - Üst: Proje */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-5 rounded-xl border-2 border-green-200">
                <Label htmlFor="project" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <FolderOpen className="w-5 h-5 text-green-600" />
                  Proje <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={newTaskData.projectId} 
                  onValueChange={(value) => {
                    setNewTaskData({ ...newTaskData, projectId: value });
                    if (touched.projectId) {
                      setFormErrors({...formErrors, projectId: null});
                    }
                  }}
                >
                  <SelectTrigger className={`bg-white border-2 ${formErrors.projectId && touched.projectId ? 'border-red-500' : 'border-green-300'}`}>
                    <SelectValue placeholder="Proje seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {[...projects].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.projectId && touched.projectId && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{formErrors.projectId}</span>
                  </div>
                )}
              </div>

              {/* Sağ Sütun - Üst: İş Akışı */}
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-5 rounded-xl border-2 border-orange-200">
                <div className="flex items-center justify-between mb-3">
                  <Label htmlFor="workflow" className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <Layers className="w-5 h-5 text-orange-600" />
                    İş Akışı <span className="text-red-500">*</span>
                  </Label>
                  <button
                    type="button"
                    onClick={() => {
                      toast.info('İş Akışı sayfasına yönlendiriliyorsunuz...');
                      setTimeout(() => {
                        window.location.href = '/workflows';
                      }, 500);
                    }}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-full p-1.5 transition-colors"
                    title="Yeni İş Akışı Ekle"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <Select 
                  value={newTaskData.workflowId || 'none'} 
                  onValueChange={(value) => {
                    setNewTaskData({ ...newTaskData, workflowId: value === 'none' ? '' : value });
                    if (touched.workflowOrAssignee) {
                      setFormErrors({...formErrors, workflowOrAssignee: null});
                    }
                  }}
                >
                  <SelectTrigger className={`bg-white border-2 ${formErrors.workflowOrAssignee && touched.workflowOrAssignee ? 'border-red-500' : 'border-orange-300'}`}>
                    <SelectValue placeholder="İş akışı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">İş akışı yok</SelectItem>
                    {[...workflows]
                      .filter(w => {
                        // İş akışı yalnızca bağlı olduğu projelerde seçilebilir.
                        // Hiç proje seçilmemiş bir workflow hiçbir projeye düşmez.
                        const linkedProjects = w.projectIds || [];
                        if (linkedProjects.length === 0) return false;
                        const selectedProj = newTaskData.projectId;
                        if (!selectedProj) return false;
                        return linkedProjects.includes(selectedProj);
                      })
                      .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                      .map(w => (
                        <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                {!formErrors.workflowOrAssignee && (
                  <p className="text-xs text-gray-600 mt-2">* İş akışı veya atanan kişiden en az biri gerekli</p>
                )}
              </div>

              {/* Sol Sütun - Alt: Öncelik */}
              <div className="bg-gradient-to-br from-rose-50 to-pink-50 p-5 rounded-xl border-2 border-rose-200">
                <Label className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <AlertCircle className="w-5 h-5 text-rose-600" />
                  Öncelik
                </Label>
                <Select value={newTaskData.priority} onValueChange={(value) => setNewTaskData({ ...newTaskData, priority: value })}>
                  <SelectTrigger className="bg-white border-2 border-rose-300">
                    <SelectValue placeholder="Öncelik seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Sağ Sütun - Alt: Atanan Kişi */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-5 rounded-xl border-2 border-indigo-200">
                <Label htmlFor="assignedTo" className="text-base font-semibold text-gray-800 flex items-center gap-2 mb-3">
                  <Users className="w-5 h-5 text-indigo-600" />
                  Atanan Kişi <span className="text-red-500">*</span>
                </Label>
                <Select 
                  value={newTaskData.assignedTo || 'none'} 
                  onValueChange={(value) => {
                    setNewTaskData({ ...newTaskData, assignedTo: value === 'none' ? '' : value });
                    if (touched.workflowOrAssignee) {
                      setFormErrors({...formErrors, workflowOrAssignee: null});
                    }
                  }}
                >
                  <SelectTrigger className={`bg-white border-2 ${formErrors.workflowOrAssignee && touched.workflowOrAssignee ? 'border-red-500' : 'border-indigo-300'}`}>
                    <SelectValue placeholder="Kullanıcı seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Atanmadı</SelectItem>
                    {[...users].sort((a, b) => a.name.localeCompare(b.name, 'tr')).map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Manuel atama yapılıyorsa (workflow varsa ve atanan kişi seçiliyse) adım seçimi */}
                {newTaskData.workflowId && newTaskData.workflowId !== 'none' && newTaskData.assignedTo && newTaskData.assignedTo !== 'none' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        İş Akışı Adımı <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={newTaskData.manualStepIndex !== null ? String(newTaskData.manualStepIndex) : ''} 
                        onValueChange={(value) => {
                          const stepIndex = parseInt(value);
                          const workflow = workflows.find(w => w.id === newTaskData.workflowId);
                          let stepType = 'Üretim';
                          if (workflow && stepIndex !== null && !isNaN(stepIndex)) {
                            stepType = workflow.steps[stepIndex]?.stepType || 'Üretim';
                          }
                          setNewTaskData({ ...newTaskData, manualStepIndex: stepIndex, taskType: stepType });
                        }}
                      >
                        <SelectTrigger className="bg-white border-2 border-indigo-200">
                          <SelectValue placeholder="Adım seçiniz" />
                        </SelectTrigger>
                        <SelectContent>
                          {workflows.find(w => w.id === newTaskData.workflowId)?.steps.map((step, index) => (
                            <SelectItem key={index} value={String(index)}>
                              {step.stepName} ({step.stepType || 'Üretim'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">* Hangi iş akışı adımına atanacağını seçin (zorunlu)</p>
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Görev Tipi
                      </Label>
                      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                          newTaskData.taskType === 'Kontrol' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {newTaskData.taskType || 'Üretim'}
                        </span>
                        <span className="text-xs text-gray-600">Seçilen adımın tipi</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Manuel atama yapılıyorsa (workflow yoksa) görev tipi, kategori, miktar tipi ve değeri seçimi */}
                {(!newTaskData.workflowId || newTaskData.workflowId === '' || newTaskData.workflowId === 'none') && newTaskData.assignedTo && newTaskData.assignedTo !== 'none' && (
                  <div className="mt-3 space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Kategori <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={newTaskData.tagId || ''} 
                        onValueChange={(value) => {
                          setNewTaskData({ ...newTaskData, tagId: value });
                          setFormErrors({...formErrors, tagId: null});
                        }}
                      >
                        <SelectTrigger className={`bg-white border-2 ${formErrors.tagId && touched.tagId ? 'border-red-500' : 'border-indigo-200'}`}>
                          <SelectValue placeholder="Kategori seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {tags.map((tag) => (
                            <SelectItem key={tag.id} value={tag.id}>
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: tag.color }}
                                />
                                {tag.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {formErrors.tagId && touched.tagId && (
                        <div className="mt-1 flex items-center gap-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4 flex-shrink-0" />
                          <span>{formErrors.tagId}</span>
                        </div>
                      )}
                      {!formErrors.tagId && (
                        <p className="text-xs text-gray-500 mt-1">Görevin kategorisini seçin</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Görev Tipi <span className="text-red-500">*</span>
                      </Label>
                      <Select 
                        value={newTaskData.taskType || 'Üretim'} 
                        onValueChange={(value) => setNewTaskData({ ...newTaskData, taskType: value })}
                      >
                        <SelectTrigger className="bg-white border-2 border-indigo-200">
                          <SelectValue placeholder="Görev tipi seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Üretim">Üretim</SelectItem>
                          <SelectItem value="Kontrol">Kontrol</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500 mt-1">İş akışı yoksa görev tipini seçin</p>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Miktar Tipi
                      </Label>
                      <Select 
                        value={newTaskData.quantityType || 'adet'} 
                        onValueChange={(value) => {
                          const defaultValue = value === 'adet' ? '1' : '00:01:00';
                          setNewTaskData({ ...newTaskData, quantityType: value, quantityValue: defaultValue });
                        }}
                      >
                        <SelectTrigger className="bg-white border-2 border-indigo-200">
                          <SelectValue placeholder="Miktar tipi seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="adet">Adet</SelectItem>
                          <SelectItem value="süre">Süre</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Miktar Değeri
                      </Label>
                      <input
                        type="text"
                        value={newTaskData.quantityValue || (newTaskData.quantityType === 'adet' ? '1' : '00:01:00')}
                        onChange={(e) => setNewTaskData({ ...newTaskData, quantityValue: e.target.value })}
                        placeholder={newTaskData.quantityType === 'adet' ? '1' : '00:01:00'}
                        className="w-full px-3 py-2 bg-white border-2 border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {newTaskData.quantityType === 'adet' ? 'Adet miktarı girin (örn: 5)' : 'Süre girin (örn: 02:30:00 = 2 saat 30 dakika)'}
                      </p>
                    </div>
                  </div>
                )}
                
                {formErrors.workflowOrAssignee && touched.workflowOrAssignee && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <span>{formErrors.workflowOrAssignee}</span>
                  </div>
                )}
                {!formErrors.workflowOrAssignee && (
                  <p className="text-xs text-gray-600 mt-2">* İş akışı veya atanan kişiden en az biri gerekli</p>
                )}
              </div>
            </div>

            {/* Modern Footer */}
            <div className="bg-gray-50 -mx-6 -mb-6 px-6 py-4 rounded-b-lg border-t-2 border-gray-200 flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                size="lg"
                onClick={() => setShowNewTaskDialog(false)}
                className="flex-1 border-2 hover:bg-gray-100"
              >
                <XIcon className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                type="submit" 
                size="lg"
                className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold shadow-lg"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Görev Oluştur
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Task Detail Drawer */}
      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showTaskDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseTaskDrawer}
        onDataChanged={() => {
          // Sessiz güncelleme - loading state tetiklemeden
          axios.get(`${API}/tasks`).then(res => {
            const normalizedTasks = res.data.map(task => {
              const project = projects.find(p => p.id === task.projectId);
              const assignedUser = users.find(u => u.id === task.assignedTo);
              const populatedHistory = (task.assignmentHistory || []).map(h => {
                const u = users.find(x => x.id === h.userId);
                return { ...h, userName: u?.name || 'Bilinmeyen Kullanıcı' };
              });
              return {
                ...task,
                originalStatus: task.status,
                status: normalizeStatus(task.status),
                projectName: project?.name || '',
                project: project || null,
                assignedToName: assignedUser?.name || null,
                assignmentHistory: populatedHistory
              };
            });
            setTasks(normalizedTasks);
          }).catch(() => {});
        }}
        onModalOpen={() => {
          const panel = drawerRef.current;
          if (panel) panel.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      {/* Delete Task Confirmation Dialog */}
      <Dialog open={showDeleteTaskDialog} onOpenChange={setShowDeleteTaskDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Görevi Sil</DialogTitle>
            <DialogDescription>
              "{taskToDelete?.title}" görevini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteTaskDialog(false)} disabled={deleting}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteTask} disabled={deleting}>
              {deleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
